import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, query, orderBy, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { groupByDate, formatDate, isToday, isPast } from '../utils/dateUtils';
import { CheckCircleIcon, XCircleIcon, ClockIcon, TrashIcon, WhatsAppIcon, PhoneIcon, SearchIcon, DownloadIcon, PrintIcon } from '../components/AdminIcons';
import { printReceipt } from '../utils/printReceipt';
import { C } from '../../constants';

const STATUS_STYLES = {
  pending:   { bg: 'rgba(245,158,11,0.1)',  color: '#D97706', label: 'Pending' },
  completed: { bg: 'rgba(16,185,129,0.1)',  color: '#059669', label: 'Completed' },
  cancelled: { bg: 'rgba(220,38,38,0.1)',   color: '#DC2626', label: 'Cancelled' },
};

function exportToCSV(bookings) {
  const headers = ['Name', 'Phone', 'Email', 'Service', 'Date', 'Message', 'Status', 'Booked At'];
  const rows = bookings.map(b => [
    b.name, b.phone, b.email || '', b.service, b.date || '', b.message || '',
    b.status || 'pending',
    b.createdAt ? new Date(b.createdAt.seconds * 1000).toLocaleString() : '',
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'bookings.csv'; a.click();
  URL.revokeObjectURL(url);
}

function BookingCard({ booking, onStatus, onDelete }) {
  const st = STATUS_STYLES[booking.status || 'pending'];
  const overdue = isPast(booking.date) && (booking.status === 'pending' || !booking.status);
  const today = isToday(booking.date);
  // Clean phone: remove +, spaces, dashes, brackets — wa.me needs digits only with country code
  const phone = booking.phone?.replace(/[\s\-\(\)\+]/g, '') || '';
  // If number starts with 0, replace with Ghana code 233
  const waPhone = phone.startsWith('0') ? '233' + phone.slice(1) : phone;
  const waMsg = encodeURIComponent(
    'Hello ' + booking.name + ', this is a reminder from Eye-Sight Care & Optical. Your appointment for ' + booking.service + ' is scheduled for ' + (booking.date || 'your booked date') + '. Please contact us at +233 547 097 213 if you need to reschedule. Thank you!'
  );
  const waLink = 'https://wa.me/' + waPhone + '?text=' + waMsg;

  return (
    <div style={{
      background: 'white', borderRadius: '8px', padding: '20px 22px',
      border: `1px solid ${overdue ? 'rgba(220,38,38,0.25)' : today ? 'rgba(200,169,110,0.3)' : 'rgba(26,68,128,0.08)'}`,
      marginBottom: '10px',
      boxShadow: overdue ? '0 0 0 1px rgba(220,38,38,0.1)' : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
        {/* Info */}
        <div style={{ flex: 1, minWidth: '200px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, color: '#0B1D3A', fontSize: '0.95rem' }}>{booking.name}</span>
            <span style={{ fontSize: '0.72rem', padding: '3px 10px', borderRadius: '20px', background: st.bg, color: st.color, fontWeight: 500 }}>{st.label}</span>
            {overdue && <span style={{ fontSize: '0.7rem', padding: '3px 10px', borderRadius: '20px', background: 'rgba(220,38,38,0.08)', color: '#DC2626', fontWeight: 600 }}>OVERDUE</span>}
            {today && <span style={{ fontSize: '0.7rem', padding: '3px 10px', borderRadius: '20px', background: 'rgba(200,169,110,0.12)', color: '#C8A96E', fontWeight: 600 }}>TODAY</span>}
          </div>
          <div style={{ fontSize: '0.84rem', color: '#4A5568', marginBottom: '3px' }}>
            <strong style={{ color: '#0B1D3A' }}>Service:</strong> {booking.service}
          </div>
          {booking.date && <div style={{ fontSize: '0.82rem', color: '#8899AA' }}><strong style={{ color: '#4A5568' }}>Date:</strong> {formatDate(booking.date)}</div>}
          {booking.phone && <div style={{ fontSize: '0.82rem', color: '#8899AA' }}><strong style={{ color: '#4A5568' }}>Phone:</strong> {booking.phone}</div>}
          {booking.email && <div style={{ fontSize: '0.82rem', color: '#8899AA' }}><strong style={{ color: '#4A5568' }}>Email:</strong> {booking.email}</div>}
          {booking.message && <div style={{ fontSize: '0.82rem', color: '#8899AA', marginTop: '6px', fontStyle: 'italic' }}>"{booking.message}"</div>}
        </div>

        {/* Actions */}
        <div className="booking-card-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <button onClick={() => onStatus(booking.id, 'completed')} title="Mark Complete"
            style={{ padding: '7px', borderRadius: '6px', border: '1px solid rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.07)', color: '#059669', cursor: 'pointer', display: 'flex' }}>
            <CheckCircleIcon s={16} />
          </button>
          <button onClick={() => onStatus(booking.id, 'pending')} title="Mark Pending"
            style={{ padding: '7px', borderRadius: '6px', border: '1px solid rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.07)', color: '#D97706', cursor: 'pointer', display: 'flex' }}>
            <ClockIcon s={16} />
          </button>
          <button onClick={() => onStatus(booking.id, 'cancelled')} title="Mark Cancelled"
            style={{ padding: '7px', borderRadius: '6px', border: '1px solid rgba(220,38,38,0.3)', background: 'rgba(220,38,38,0.07)', color: '#DC2626', cursor: 'pointer', display: 'flex' }}>
            <XCircleIcon s={16} />
          </button>
          {booking.phone && (
            <a href={waLink} target="_blank" rel="noreferrer" title="Send WhatsApp Reminder"
              style={{ padding: '7px', borderRadius: '6px', border: '1px solid rgba(37,211,102,0.3)', background: 'rgba(37,211,102,0.07)', color: '#25D366', cursor: 'pointer', display: 'flex', textDecoration: 'none' }}>
              <WhatsAppIcon s={16} />
            </a>
          )}
          {booking.phone && (
            <a href={`tel:${booking.phone}`} title="Call client"
              style={{ padding: '7px', borderRadius: '6px', border: '1px solid rgba(26,68,128,0.2)', background: 'rgba(26,68,128,0.05)', color: '#1A4480', cursor: 'pointer', display: 'flex', textDecoration: 'none' }}>
              <PhoneIcon s={16} />
            </a>
          )}
          <button onClick={() => { if (window.confirm('Delete this booking?')) onDelete(booking.id); }} title="Delete"
            style={{ padding: '7px', borderRadius: '6px', border: '1px solid rgba(220,38,38,0.2)', background: 'transparent', color: '#DC2626', cursor: 'pointer', display: 'flex' }}>
            <TrashIcon s={16} />
          </button>
          <button onClick={() => printReceipt(booking)} title="Print Receipt"
            style={{ padding: '7px', borderRadius: '6px', border: '1px solid rgba(26,68,128,0.2)', background: 'rgba(26,68,128,0.05)', color: '#1A4480', cursor: 'pointer', display: 'flex' }}>
            <PrintIcon s={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Appointments() {
  const [bookings, setBookings] = useState([]);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap => setBookings(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, []);

  const updateStatus = async (id, status) => {
    await updateDoc(doc(db, 'bookings', id), { status });
  };

  const deleteBooking = async (id) => {
    await deleteDoc(doc(db, 'bookings', id));
  };

  const filtered = bookings.filter(b => {
    const matchSearch = !search || b.name?.toLowerCase().includes(search.toLowerCase()) || b.phone?.includes(search) || b.service?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || (b.status || 'pending') === filterStatus;
    return matchSearch && matchStatus;
  });

  const grouped = groupByDate(filtered);

  return (
    <div style={{ padding: '32px', fontFamily: 'Jost, sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', fontWeight: 700, color: '#0B1D3A', marginBottom: '4px' }}>Appointments</h1>
          <p style={{ color: '#8899AA', fontSize: '0.88rem' }}>{bookings.length} total bookings</p>
        </div>
        <button onClick={() => exportToCSV(filtered)}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: C.navy, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'Jost, sans-serif', fontWeight: 500 }}>
          <DownloadIcon s={16} /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
          <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#8899AA' }}><SearchIcon s={16} /></div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, phone, service..."
            style={{ width: '100%', padding: '10px 12px 10px 38px', border: '1px solid rgba(26,68,128,0.15)', borderRadius: '6px', fontSize: '0.86rem', fontFamily: 'Jost, sans-serif', outline: 'none', color: '#1C2B3A' }} />
        </div>
        {['all', 'pending', 'completed', 'cancelled'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)} style={{
            padding: '10px 18px', borderRadius: '6px', border: '1px solid',
            borderColor: filterStatus === s ? C.blue : 'rgba(26,68,128,0.15)',
            background: filterStatus === s ? C.blue : 'white',
            color: filterStatus === s ? 'white' : '#4A5568',
            cursor: 'pointer', fontSize: '0.8rem', textTransform: 'capitalize', fontFamily: 'Jost, sans-serif',
          }}>{s}</button>
        ))}
      </div>

      {/* Grouped bookings */}
      {grouped.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#8899AA', fontSize: '0.9rem' }}>No bookings found.</div>
      ) : (
        grouped.map(([date, items]) => (
          <div key={date} style={{ marginBottom: '28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{ height: '1px', width: '16px', background: C.gold }} />
              <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.05rem', fontWeight: 600, color: '#0B1D3A', whiteSpace: 'nowrap' }}>
                {date === 'No Date' ? 'No Date Specified' : formatDate(date)}
                <span style={{ fontSize: '0.75rem', color: '#8899AA', fontFamily: 'Jost, sans-serif', fontWeight: 400, marginLeft: '10px' }}>{items.length} appointment{items.length !== 1 ? 's' : ''}</span>
              </h3>
              <div style={{ height: '1px', flex: 1, background: 'rgba(26,68,128,0.08)' }} />
            </div>
            {items.map(b => (
              <BookingCard key={b.id} booking={b} onStatus={updateStatus} onDelete={deleteBooking} />
            ))}
          </div>
        ))
      )}
    </div>
  );
}
